import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@components/Button/Button'
import { Badge } from '@components/Badge/Badge'
import { Pagination } from '@components/Pagination/Pagination'
import { useContactStore } from '../store/useContactStore'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { getColorForIndex } from '../utils/tagColors'
import { getContactState, formatPhoneForGrid, formatSocialForGrid, STATE_SHADOW_STYLE, DEFAULT_PAGE_SIZE } from './contactGridUtils'
import { ContactGridSkeleton } from './ContactGridSkeleton'
import { ContactGridEmptyState } from './ContactGridEmptyState'
import { ContactRowActions } from './ContactRowActions'
import { ResponsiveContactCard } from './ResponsiveContactCard'
import type { EnumOption } from '../types/enum.types'
import type { CatalogItem } from '../types/adapter.types'
import { useTranslations } from '../providers/ContactServiceProvider'

interface ContactGridProps {
  recordId: string
  mode: 'edit' | 'view'
  useForOptions?: EnumOption[]
  phoneCategoryOptions?: EnumOption[]
  socialNetworkTypes?: CatalogItem[]
  isLoading?: boolean
  onAddContact: () => void
  onEditContact: (id: string) => void
  onViewContact: (id: string) => void
  onDeleteContact: (id: string) => void
  onRestoreContact: (id: string) => void
}

function EmailPills({ emails }: { emails: string[] }) {
  if (emails.length === 0) return <span>—</span>
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-block max-w-[180px] truncate rounded-full bg-slate-100 px-2 py-0.5 text-xs text-content-primary dark:bg-slate-800 dark:text-dark-content-primary">
        {emails[0]}
      </span>
      {emails.length > 1 && (
        <Badge color="blue" label={`+${emails.length - 1}`} />
      )}
    </span>
  )
}

function ClassificationBadges({ useFor, useForOptions }: { useFor: number[]; useForOptions: EnumOption[] }) {
  if (useFor.length === 0) return <span>—</span>
  const sorted = [...useFor].sort((a, b) => {
    const idxA = useForOptions.findIndex((o) => o.value === a)
    const idxB = useForOptions.findIndex((o) => o.value === b)
    return idxA - idxB
  })
  return (
    <span className="flex flex-wrap gap-1">
      {sorted.map((useForValue) => {
        const option = useForOptions.find((o) => o.value === useForValue)
        const colorIndex = useForOptions.findIndex((o) => o.value === useForValue)
        const color = getColorForIndex(colorIndex >= 0 ? colorIndex : 0)
        return (
          <span
            key={useForValue}
            className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${color.bg} ${color.text} ${color.darkBg} ${color.darkText}`}
          >
            {option?.label ?? String(useForValue)}
          </span>
        )
      })}
    </span>
  )
}

function PhoneCell({ phones, categoryLabelMap }: { phones: { category: number; countryId: string; areaCode: string; number: string; extension: string }[]; categoryLabelMap?: Map<number, string> }) {
  if (phones.length === 0) return <span>—</span>
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-sm text-content-tertiary dark:text-dark-content-tertiary">
        {formatPhoneForGrid(phones[0], categoryLabelMap)}
      </span>
      {phones.length > 1 && (
        <Badge color="blue" label={`+${phones.length - 1}`} />
      )}
    </span>
  )
}

function SocialCell({ socialNetworks, socialNetworkLabelMap, notEnabledLabel }: { socialNetworks: { socialNetworkTypeId: string; url: string }[]; socialNetworkLabelMap?: Map<string, string>; notEnabledLabel: string }) {
  if (socialNetworks.length === 0) return <span>—</span>
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-sm text-content-tertiary dark:text-dark-content-tertiary">
        {formatSocialForGrid(socialNetworks[0], socialNetworkLabelMap, notEnabledLabel)}
      </span>
      {socialNetworks.length > 1 && (
        <Badge color="blue" label={`+${socialNetworks.length - 1}`} />
      )}
    </span>
  )
}

export function ContactGrid({
  recordId,
  mode,
  useForOptions,
  phoneCategoryOptions,
  socialNetworkTypes,
  isLoading = false,
  onAddContact,
  onEditContact,
  onViewContact,
  onDeleteContact,
  onRestoreContact,
}: ContactGridProps) {
  const contacts = useContactStore((s) => s.contacts)
  const pendingAdds = useContactStore((s) => s.pendingAdds)
  const pendingEdits = useContactStore((s) => s.pendingEdits)
  const pendingDeletes = useContactStore((s) => s.pendingDeletes)
  const t = useTranslations()

  const isTabletOrAbove = useMediaQuery('(min-width: 768px)')

  const showClassification = (useForOptions?.length ?? 0) > 1

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const totalPages = Math.max(1, Math.ceil(contacts.length / DEFAULT_PAGE_SIZE))
  const paginatedContacts = useMemo(() => {
    const start = (currentPage - 1) * DEFAULT_PAGE_SIZE
    return contacts.slice(start, start + DEFAULT_PAGE_SIZE)
  }, [contacts, currentPage])

  // Reset to page 1 if current page becomes invalid
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1)
  }

  const socialNetworkLabelMap = useMemo(() => {
    if (!socialNetworkTypes) return undefined
    return new Map(socialNetworkTypes.map((snt) => [snt.id, snt.name]))
  }, [socialNetworkTypes])

  const categoryLabelMap = useMemo(() => {
    if (!phoneCategoryOptions) return undefined
    return new Map(phoneCategoryOptions.map((o) => [o.value, o.label]))
  }, [phoneCategoryOptions])

  return (
    <div>
      {/* Header: Add button only — the "Contacto" section title was redundant with the tab/page
          heading the consumer already shows above this component. */}
      <div className="flex items-center justify-end mb-3">
        {mode === 'edit' && (
          <Button
            type="default"
            leftIcon={<Plus size={16} />}
            onClick={onAddContact}
            disabled={isLoading}
          >
            {t.grid.button.addContact}
          </Button>
        )}
      </div>

      {/* Loading state */}
      {isLoading && <ContactGridSkeleton layout={isTabletOrAbove ? 'table' : 'card'} />}

      {/* Empty state */}
      {!isLoading && contacts.length === 0 && (
        <ContactGridEmptyState />
      )}

      {/* Contact list */}
      {!isLoading && contacts.length > 0 && (
        <>
          {isTabletOrAbove ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]" aria-label={t.grid.aria.table}>
                <thead>
                  {/* text-content-primary — same header color the shared Table component uses
                      (TableHeader's own span, e.g. ContactModal's phone/social tables). The
                      previous text-content-secondary (#a1a1aa) read as washed-out/low-contrast. */}
                  <tr className="border-b border-border-primary dark:border-dark-border-primary">
                    {showClassification && (
                      <th scope="col" className="py-3 px-4 text-left text-sm font-semibold text-content-primary dark:text-dark-content-primary min-w-[240px]">{t.grid.column.useFor}</th>
                    )}
                    <th scope="col" className="py-3 px-4 text-left text-sm font-semibold text-content-primary dark:text-dark-content-primary min-w-[130px]">{t.grid.column.name}</th>
                    <th scope="col" className="py-3 px-4 text-left text-sm font-semibold text-content-primary dark:text-dark-content-primary min-w-[160px]">{t.grid.column.email}</th>
                    <th scope="col" className="py-3 px-4 text-left text-sm font-semibold text-content-primary dark:text-dark-content-primary min-w-[180px] whitespace-nowrap">{t.grid.column.phone}</th>
                    <th scope="col" className="py-3 px-4 text-left text-sm font-semibold text-content-primary dark:text-dark-content-primary min-w-[180px]">{t.grid.column.socialNetworks}</th>
                    {/* px-2 (not px-4) + the buttons' own gap-1 (ContactRowActions) — tighter
                        than before so the column's real rendered width (driven by its 3-icon
                        row, the widest case) sits close to min-w instead of padding it out
                        another 16px per side, which is what pushed the row's buttons away
                        from a now-centered label. */}
                    <th scope="col" className="py-3 px-2 text-center text-sm font-semibold text-content-primary dark:text-dark-content-primary sticky right-0 bg-white dark:bg-dark-bg-primary z-10 min-w-[92px]">{t.grid.column.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedContacts.map((contact, contactIndex) => {
                    const state = getContactState(contact, pendingAdds, pendingEdits, pendingDeletes)
                    const stateStyle = state ? STATE_SHADOW_STYLE[state] : ''
                    const isDeleted = state === 'deleted'
                    const groupBg = contactIndex % 2 === 0
                      ? 'bg-bg-primary dark:bg-dark-bg-primary'
                      : 'bg-slate-50 dark:bg-slate-900'

                    return (
                      <tr
                        key={contact.id ?? contactIndex}
                        className={`border-b border-border-secondary dark:border-dark-border-primary ${stateStyle} ${groupBg} ${isDeleted ? 'opacity-60' : ''}`}
                      >
                        {showClassification && (
                          <td className="py-3 px-4 text-sm">
                            <ClassificationBadges useFor={contact.useFor} useForOptions={useForOptions!} />
                          </td>
                        )}
                        <td className={`py-3 px-4 text-sm text-content-primary dark:text-dark-content-primary ${isDeleted ? 'line-through text-content-tertiary dark:text-dark-content-tertiary' : ''}`}>
                          {contact.name}
                        </td>
                        <td className={`py-3 px-4 text-sm text-content-tertiary dark:text-dark-content-tertiary ${isDeleted ? 'line-through' : ''}`}>
                          <EmailPills emails={contact.emails} />
                        </td>
                        <td className={`py-3 px-4 text-sm whitespace-nowrap ${isDeleted ? 'line-through' : ''}`}>
                          <PhoneCell phones={contact.phones} categoryLabelMap={categoryLabelMap} />
                        </td>
                        <td className={`py-3 px-4 text-sm whitespace-nowrap ${isDeleted ? 'line-through' : ''}`}>
                          <SocialCell socialNetworks={contact.socialNetworks} socialNetworkLabelMap={socialNetworkLabelMap} notEnabledLabel={t.grid.socialNetworkNotEnabled} />
                        </td>
                        <td className="py-3 px-2 text-center sticky right-0 bg-inherit z-10 shadow-[-2px_0_4px_rgba(0,0,0,0.05)]">
                          {contact.id != null && (
                            <ContactRowActions
                              contactId={contact.id}
                              mode={mode}
                              isPendingDelete={isDeleted}
                              onEdit={onEditContact}
                              onView={onViewContact}
                              onDelete={onDeleteContact}
                              onRestore={onRestoreContact}
                            />
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {paginatedContacts.map((contact, index) => {
                const state = getContactState(contact, pendingAdds, pendingEdits, pendingDeletes)
                return (
                  <ResponsiveContactCard
                    key={contact.id ?? index}
                    contact={contact}
                    state={state}
                    mode={mode}
                    useForOptions={useForOptions}
                    phoneCategoryOptions={phoneCategoryOptions}
                    socialNetworkTypes={socialNetworkTypes}
                    onEdit={onEditContact}
                    onView={onViewContact}
                    onDelete={onDeleteContact}
                    onRestore={onRestoreContact}
                  />
                )
              })}
            </div>
          )}

          {/* Pagination — only worth showing once there's a second page to go to
              (more than DEFAULT_PAGE_SIZE contacts); a single page of results doesn't need it. */}
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                backText={t.grid.pagination.previous}
                nextText={t.grid.pagination.next}
                className="w-full"
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
